<template>
  <div id="app" class="app-root">
    <TelegramWidget />
    <CallbackModal />

    <!-- Навигация (скрыта на главной странице, там своя) -->
    <nav v-if="!isHome" class="app-nav">
      <div class="app-nav-inner">
        <router-link to="/" class="app-logo">Mobi<span>FIX</span> PRO</router-link>

        <div class="app-nav-links">
          <router-link to="/" class="app-link">{{ navT.home }}</router-link>
          <router-link to="/services" class="app-link">{{ navT.services }}</router-link>
        </div>

        <div class="app-nav-auth">
          <template v-if="isAuthenticated">
            <router-link to="/orders" class="app-link">{{ navT.orders }}</router-link>
            <router-link to="/profile" class="app-link">{{ navT.profile }}</router-link>
            <button @click="logout" class="app-btn-logout">{{ navT.logout }}</button>
          </template>
          <template v-else>
            <router-link to="/login" class="app-btn-outline">{{ navT.login }}</router-link>
            <router-link to="/register" class="app-btn-primary">{{ navT.register }}</router-link>
          </template>
          <button class="app-lang-toggle" @click="locale.setLang(locale.lang === 'uk' ? 'en' : 'uk')">
            {{ locale.lang === 'uk' ? '🇬🇧 EN' : '🇺🇦 UA' }}
          </button>
        </div>
      </div>
    </nav>

    <!-- Основное содержимое -->
    <main class="app-main" :class="{ 'no-nav': isHome }">
      <router-view />
    </main>

    <!-- Футер (скрыт на главной, там свой) -->
    <footer v-if="!isHome" class="app-footer">
      <div class="app-footer-inner">
        <div class="app-footer-brand">
          <div class="app-footer-logo">Mobi<span>FIX</span> PRO</div>
          <p>Профессиональный ремонт телефонов<br>и планшетов в Харькове</p>
        </div>
        <div class="app-footer-col">
          <div class="app-footer-title">Услуги</div>
          <router-link to="/services" class="app-footer-link">Замена экрана</router-link>
          <router-link to="/services" class="app-footer-link">Замена батареи</router-link>
          <router-link to="/services" class="app-footer-link">Ремонт после воды</router-link>
          <router-link to="/services" class="app-footer-link">Ремонт камеры</router-link>
        </div>
        <div class="app-footer-col">
          <div class="app-footer-title">Контакты</div>
          <a href="tel:+380682772822" class="app-footer-link">📞 +38 (068) 277-28-22</a>
          <a href="mailto:info@mobifix.ua" class="app-footer-link">✉️ info@mobifix.ua</a>
          <div class="app-footer-link">📍 Харьков, просп. Ювілейний, 57а</div>
          <div class="app-footer-link">🕐 Пн–Сб 9:00–19:00</div>
        </div>
        <div class="app-footer-col">
          <div class="app-footer-title">Аккаунт</div>
          <router-link to="/orders/create" class="app-footer-link">Оформить заказ</router-link>
          <router-link to="/orders" class="app-footer-link">Мои заказы</router-link>
          <router-link to="/login" class="app-footer-link">Войти</router-link>
        </div>
      </div>
      <div class="app-footer-bottom">
        <span>© 2026 MobiFIX PRO — Харків. Всі права захищені.</span>
        <span>Гарантія 12 місяців · Діагностика безкоштовно · Пн–Сб 9:00–19:00</span>
      </div>
    </footer>

  </div>
</template>

<script>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from './stores/auth';
import { useLocaleStore } from './stores/locale';
import TelegramWidget from './components/TelegramWidget.vue';
import CallbackModal from './components/CallbackModal.vue';

export default {
  name: 'App',
  components: { TelegramWidget, CallbackModal },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const authStore = useAuthStore();

    const locale = useLocaleStore();
    const isHome = computed(() => route.name === 'Home');
    const isAuthenticated = computed(() => !!authStore.token);

    const navT = computed(() => ({
      home: locale.lang === 'uk' ? 'Головна' : 'Home',
      services: locale.lang === 'uk' ? 'Послуги' : 'Services',
      orders: locale.lang === 'uk' ? 'Мої замовлення' : 'My Orders',
      profile: locale.lang === 'uk' ? 'Профіль' : 'Profile',
      login: locale.lang === 'uk' ? 'Увійти' : 'Log In',
      register: locale.lang === 'uk' ? 'Реєстрація' : 'Sign Up',
      logout: locale.lang === 'uk' ? 'Вийти' : 'Log Out',
    }));

    const logout = () => {
      authStore.logout();
      router.push('/');
    };

    return { locale, navT, isHome, isAuthenticated, logout };
  }
};
</script>

<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: 'Segoe UI', system-ui, sans-serif; background: #f4f3ff; color: #26215c; }
#app { display: flex; flex-direction: column; min-height: 100vh; }
.app-root { min-height: 100vh; }

/* ---- NAV ---- */
.app-nav {
  position: sticky;
  top: 0;
  z-index: 200;
  background: #fff;
  border-bottom: 0.5px solid #e0dff8;
  box-shadow: 0 2px 16px rgba(83,74,183,0.06);
}
.app-nav-inner {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0.85rem 2rem;
  display: flex;
  align-items: center;
  gap: 2rem;
}
.app-logo {
  font-size: 18px;
  font-weight: 600;
  color: #534ab7;
  letter-spacing: 0.5px;
  text-decoration: none;
  flex-shrink: 0;
}
.app-logo span { color: #1d9e75; }
.app-nav-links {
  display: flex;
  gap: 1.5rem;
  flex: 1;
}
.app-link {
  color: #666;
  font-size: 14px;
  text-decoration: none;
  transition: color 0.15s;
  font-weight: 500;
}
.app-link:hover, .app-link.router-link-active { color: #534ab7; }
.app-nav-auth {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}
.app-btn-primary {
  background: #534ab7;
  color: #fff;
  border: none;
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s;
  display: inline-block;
}
.app-btn-primary:hover { background: #7f77dd; }
.app-btn-outline {
  background: transparent;
  color: #534ab7;
  border: 1.5px solid #534ab7;
  padding: 7px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.15s;
  display: inline-block;
}
.app-btn-outline:hover { background: #eeedfe; }
.app-btn-logout {
  background: transparent;
  color: #e05252;
  border: 1.5px solid #e05252;
  padding: 7px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}
.app-btn-logout:hover { background: #ffeaea; }
.app-lang-toggle { background: #f4f3ff; color: #534ab7; border: 1.5px solid #e0dff8; padding: 6px 12px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
.app-lang-toggle:hover { background: #eeedfe; border-color: #534ab7; }

/* ---- MAIN ---- */
.app-main {
  flex: 1;
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
  padding: 2.5rem 2rem;
}
.app-main.no-nav {
  max-width: 100%;
  padding: 0;
}

/* ---- FOOTER ---- */
.app-footer {
  background: #1a1730;
  color: #ccc;
  margin-top: 4rem;
}
.app-footer-inner {
  max-width: 1100px;
  margin: 0 auto;
  padding: 3rem 2rem 2rem;
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: 2rem;
}
.app-footer-brand p {
  font-size: 13px;
  color: #888;
  line-height: 1.7;
  margin-top: 0.5rem;
}
.app-footer-logo {
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.5px;
}
.app-footer-logo span { color: #1d9e75; }
.app-footer-col { display: flex; flex-direction: column; gap: 8px; }
.app-footer-title { font-size: 12px; font-weight: 600; color: #fff; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.app-footer-link { font-size: 13px; color: #888; text-decoration: none; transition: color 0.15s; }
.app-footer-link:hover { color: #c7c4f5; }
.app-footer-bottom {
  max-width: 1100px;
  margin: 0 auto;
  padding: 1.2rem 2rem;
  border-top: 1px solid #2d2a4a;
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #666;
}

@media (max-width: 768px) {
  .app-nav-links { display: none; }
  .app-footer-inner { grid-template-columns: 1fr 1fr; }
  .app-footer-bottom { flex-direction: column; gap: 6px; }
  .app-main { padding: 1.5rem 1rem; }
}
</style>
